/**
 * FRONTROOMS Web Audio API Sound Synthesizer
 * Programmatic real-time audio synthesis for infinite liminal spaces.
 * No external file assets needed.
 */

export class WebAudioSynth {
  constructor() {
    this.ctx = null;
    this.initialized = false;

    // Master volume levels (0 to 1)
    this.humMaxVolume = 0.35;
    this.footstepMaxVolume = 0.5;

    // Nodes references
    this.masterGain = null;
    this.humGain = null;
    this.flickerGain = null;

    // Oscillators and noise references
    this.humOsc55 = null;
    this.humOsc110 = null;
    this.ventNoise = null;
    this.noiseBuffer = null;

    // Config options
    this.flickerInterval = null;

    // Ambient Music states
    this.musicAudio = null;
    this.musicSource = null;
    this.musicGain = null;
    this.musicMaxVolume = 0.18; // default 60% of 0.3
    this.musicPlayingState = 'silent'; // 'silent' | 'playing' | 'fading'
    this.musicTimeout = null;
    this.musicSilenceTimeout = null;
    this.musicNextCycleTimeout = null;
  }

  /**
   * Initializes the Audio Context and builds the audio graph.
   * Must be called in response to a user interaction.
   */
  async init() {
    if (this.initialized) return;

    try {
      if (this.ctx) {
        if (this.ctx.state === 'suspended') {
          await this.ctx.resume();
        }
        if (this.masterGain) {
          this.masterGain.gain.setValueAtTime(1.0, this.ctx.currentTime);
        }
        // Re-trigger music loop cycle
        this.runMusicCycle();
        this.initialized = true;
        console.log("WebAudioSynth: Recycled existing AudioContext successfully.");
        return;
      }

      // Create context
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      this.ctx = new AudioContextClass();

      // Resume context if suspended (browser security)
      if (this.ctx.state === 'suspended') {
        await this.ctx.resume();
      }

      // 1. Create Master Gain Node
      this.masterGain = this.ctx.createGain();
      this.masterGain.gain.setValueAtTime(1.0, this.ctx.currentTime);
      this.masterGain.connect(this.ctx.destination);

      // 2. Pre-generate Noise Buffer (2 seconds of white noise)
      const sampleRate = this.ctx.sampleRate;
      const bufferSize = sampleRate * 2;
      this.noiseBuffer = this.ctx.createBuffer(1, bufferSize, sampleRate);
      const data = this.noiseBuffer.getChannelData(0);
      for (let i = 0; i < bufferSize; i++) {
        data[i] = Math.random() * 2 - 1;
      }

      // 3. Set up steady HVAC & Fluorescent Hum
      this.setupHumHVAC();

      // 4. Set up ambient music stream & start scheduler loop
      this.startAmbientMusicLoop();

      this.initialized = true;
      console.log("WebAudioSynth: Initialized successfully.");
    } catch (e) {
      console.error("WebAudioSynth: Failed to initialize audio context.", e);
    }
  }

  /**
   * Mutes and suspends the audio synthesis pipeline.
   */
  stop() {
    if (!this.initialized) return;

    try {
      // Pause ambient music
      if (this.musicAudio) {
        this.musicAudio.pause();
      }

      // Clear all active music cycle timeouts
      clearTimeout(this.musicTimeout);
      clearTimeout(this.musicSilenceTimeout);
      clearTimeout(this.musicNextCycleTimeout);

      // Mute the master gain immediately
      if (this.masterGain) {
        this.masterGain.gain.setValueAtTime(0, this.ctx.currentTime);
      }

      // Suspend context to stop active processing and save CPU
      if (this.ctx && this.ctx.state === 'running') {
        this.ctx.suspend();
      }

      this.initialized = false;
      console.log("WebAudioSynth: Suspended and muted audio context.");
    } catch (e) {
      console.error("WebAudioSynth: Error stopping audio synthesizer.", e);
    }
  }

  /**
   * Builds the HVAC pink noise and fluorescent tube transformer buzz.
   */
  setupHumHVAC() {
    // A. Fluorescent hum (55Hz sine + 110Hz harmonic)
    this.humOsc55 = this.ctx.createOscillator();
    this.humOsc55.type = 'sine';
    this.humOsc55.frequency.setValueAtTime(55, this.ctx.currentTime); // Standard utility A1 hum

    this.humOsc110 = this.ctx.createOscillator();
    this.humOsc110.type = 'sine';
    this.humOsc110.frequency.setValueAtTime(110, this.ctx.currentTime); // Low harmonic buzz

    const humOscGain55 = this.ctx.createGain();
    const humOscGain110 = this.ctx.createGain();
    
    humOscGain55.gain.setValueAtTime(0.4, this.ctx.currentTime);
    humOscGain110.gain.setValueAtTime(0.15, this.ctx.currentTime);

    // B. HVAC Ventilation Airflow Noise (Low-pass filtered white noise)
    this.ventNoise = this.ctx.createBufferSource();
    this.ventNoise.buffer = this.noiseBuffer;
    this.ventNoise.loop = true;

    const ventFilter = this.ctx.createBiquadFilter();
    ventFilter.type = 'lowpass';
    ventFilter.frequency.setValueAtTime(180, this.ctx.currentTime); // Keep it deep and muffled
    ventFilter.Q.setValueAtTime(1.0, this.ctx.currentTime);

    const ventGain = this.ctx.createGain();
    ventGain.gain.setValueAtTime(0.65, this.ctx.currentTime);

    // C. Mixer Node for Hum & Vents
    this.humGain = this.ctx.createGain();
    this.humGain.gain.setValueAtTime(this.humMaxVolume, this.ctx.currentTime);

    // D. Flickering modulator gain
    this.flickerGain = this.ctx.createGain();
    this.flickerGain.gain.setValueAtTime(1.0, this.ctx.currentTime);

    // Connect oscillators
    this.humOsc55.connect(humOscGain55);
    this.humOsc110.connect(humOscGain110);
    
    humOscGain55.connect(this.humGain);
    humOscGain110.connect(this.humGain);

    // Connect ventilation noise
    this.ventNoise.connect(ventFilter);
    ventFilter.connect(ventGain);
    ventGain.connect(this.humGain);

    // Connect to Master via flicker gain
    this.humGain.connect(this.flickerGain);
    this.flickerGain.connect(this.masterGain);

    // Start sound generators
    this.humOsc55.start(0);
    this.humOsc110.start(0);
    this.ventNoise.start(0);

    // Periodically add subtle pitch drift for artificial fluorescent degradation
    this.startSubtleDrift();
  }

  /**
   * Slowly fluctuates the fluorescent hum pitch to make it sound slightly broken.
   */
  startSubtleDrift() {
    setInterval(() => {
      if (!this.initialized || this.ctx.state === 'suspended') return;
      const now = this.ctx.currentTime;
      // Subtle float around 55Hz and 110Hz
      const drift55 = 55 + (Math.random() * 0.4 - 0.2);
      const drift110 = 110 + (Math.random() * 0.8 - 0.4);
      
      this.humOsc55.frequency.linearRampToValueAtTime(drift55, now + 1.5);
      this.humOsc110.frequency.linearRampToValueAtTime(drift110, now + 1.5);
    }, 2000);
  }

  /**
   * Dynamically triggers a sharp audio "crack/buzz/pop" mimicking a flickering fluorescent bulb.
   * @param {number} severity - 0 to 1 value representing severity of the flicker
   */
  playFlickerSound(severity = 0.5) {
    if (!this.initialized || this.ctx.state === 'suspended') return;

    const now = this.ctx.currentTime;
    
    // Create a high-passed noise pop (the bulb sparking)
    const popSource = this.ctx.createBufferSource();
    popSource.buffer = this.noiseBuffer;
    
    const popFilter = this.ctx.createBiquadFilter();
    popFilter.type = 'bandpass';
    popFilter.frequency.setValueAtTime(3000, now);
    popFilter.Q.setValueAtTime(2.0, now);

    const popGain = this.ctx.createGain();
    popGain.gain.setValueAtTime(0, now);
    popGain.gain.linearRampToValueAtTime(severity * 0.15, now + 0.005);
    popGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.08);

    popSource.connect(popFilter);
    popFilter.connect(popGain);
    popGain.connect(this.masterGain);
    popSource.start(now);
    popSource.stop(now + 0.1);

    // Rapidly drop main hum volume to simulate momentary electrical blackouts
    const numFlickers = Math.floor(2 + Math.random() * 4);
    let timeOffset = 0;
    
    for (let i = 0; i < numFlickers; i++) {
      const flickerDuration = 0.01 + Math.random() * 0.03;
      const t = now + timeOffset;

      // Drop hum gain near 0
      this.flickerGain.gain.setValueAtTime(0.08, t);
      // Surge hum pitch slightly
      this.humOsc110.frequency.setValueAtTime(160 + Math.random() * 40, t);

      // Restore hum gain
      this.flickerGain.gain.setValueAtTime(1.0, t + flickerDuration);
      this.humOsc110.frequency.setValueAtTime(110, t + flickerDuration);

      timeOffset += flickerDuration + (0.01 + Math.random() * 0.02);
    }
  }

  /**
   * Synthesizes a footstep sound based on the material walked upon.
   * @param {string} material - 'carpet', 'tile' (terrazzo/marble), or 'metal'
   */
  playFootstep(material = 'carpet') {
    if (!this.initialized || this.ctx.state === 'suspended') return;

    const now = this.ctx.currentTime;
    
    // Footstep Sound Generator
    const noiseSource = this.ctx.createBufferSource();
    noiseSource.buffer = this.noiseBuffer;

    const footFilter = this.ctx.createBiquadFilter();
    const footGain = this.ctx.createGain();

    // Custom properties depending on surface
    if (material === 'carpet') {
      // Soft, heavy bass thud
      footFilter.type = 'lowpass';
      footFilter.frequency.setValueAtTime(110, now); // Low thud
      footFilter.Q.setValueAtTime(1.8, now);

      footGain.gain.setValueAtTime(0, now);
      footGain.gain.linearRampToValueAtTime(this.footstepMaxVolume * 0.7, now + 0.01);
      footGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.18); // Soft decay
    } 
    else if (material === 'tile') {
      // Crisp slap/click with subtle bass echo
      footFilter.type = 'bandpass';
      footFilter.frequency.setValueAtTime(800, now); // Mid-frequency slap
      footFilter.Q.setValueAtTime(1.2, now);

      footGain.gain.setValueAtTime(0, now);
      footGain.gain.linearRampToValueAtTime(this.footstepMaxVolume * 0.45, now + 0.005);
      footGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.12);

      // Add a low-frequency heavy thud overlay
      this.playLowThud(0.18, this.footstepMaxVolume * 0.35);
    } 
    else { // 'metal'
      // Metallic ringing tap
      footFilter.type = 'peaking';
      footFilter.frequency.setValueAtTime(1400, now);
      footFilter.Q.setValueAtTime(4.0, now);
      footFilter.gain.setValueAtTime(12, now);

      footGain.gain.setValueAtTime(0, now);
      footGain.gain.linearRampToValueAtTime(this.footstepMaxVolume * 0.4, now + 0.003);
      footGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.15);

      // Metal ring oscillator
      const ringOsc = this.ctx.createOscillator();
      ringOsc.type = 'sine';
      ringOsc.frequency.setValueAtTime(450, now);
      
      const ringGain = this.ctx.createGain();
      ringGain.gain.setValueAtTime(0, now);
      ringGain.gain.linearRampToValueAtTime(this.footstepMaxVolume * 0.08, now + 0.003);
      ringGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.08);

      ringOsc.connect(ringGain);
      ringGain.connect(this.masterGain);
      ringOsc.start(now);
      ringOsc.stop(now + 0.1);

      this.playLowThud(0.2, this.footstepMaxVolume * 0.3);
    }

    noiseSource.connect(footFilter);
    footFilter.connect(footGain);
    footGain.connect(this.masterGain);

    noiseSource.start(now);
    noiseSource.stop(now + 0.25);
  }

  /**
   * Helper that triggers a low-frequency rumble thud for hard flooring clicks.
   */
  playLowThud(duration, volume) {
    const now = this.ctx.currentTime;
    
    // Sub-oscillator for heel weight
    const osc = this.ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(75, now);
    osc.frequency.exponentialRampToValueAtTime(30, now + duration);

    const oscGain = this.ctx.createGain();
    oscGain.gain.setValueAtTime(0, now);
    oscGain.gain.linearRampToValueAtTime(volume, now + 0.01);
    oscGain.gain.exponentialRampToValueAtTime(0.0001, now + duration);

    osc.connect(oscGain);
    oscGain.connect(this.masterGain);

    osc.start(now);
    osc.stop(now + duration + 0.05);
  }

  /**
   * Synthesizes a high-fidelity elevator arrival bell using clean FM synthesis.
   * Sounds like a classic brass dome physical ding echoing in a vast room.
   */
  playElevatorDing() {
    if (!this.initialized || this.ctx.state === 'suspended') return;

    const now = this.ctx.currentTime;

    // Carrier Oscillator (primary pitch)
    const carrier = this.ctx.createOscillator();
    carrier.type = 'sine';
    carrier.frequency.setValueAtTime(920, now); // High chime

    // Modulator Oscillator (harmonic thickness)
    const modulator = this.ctx.createOscillator();
    modulator.type = 'sine';
    modulator.frequency.setValueAtTime(1380, now); // Perfect 5th overhead harmonic

    const modGain = this.ctx.createGain();
    modGain.gain.setValueAtTime(150, now); // Frequency modulation depth
    modGain.gain.exponentialRampToValueAtTime(1, now + 2.0);

    // Chime Main Envelope
    const chimeGain = this.ctx.createGain();
    chimeGain.gain.setValueAtTime(0, now);
    chimeGain.gain.linearRampToValueAtTime(0.3, now + 0.005);
    chimeGain.gain.exponentialRampToValueAtTime(0.02, now + 0.8);
    chimeGain.gain.exponentialRampToValueAtTime(0.0001, now + 3.0); // Long empty room echo

    // Connect FM synthesis graph
    modulator.connect(modGain);
    modGain.connect(carrier.frequency); // Modulate carrier frequency

    // Filter to simulate vast, slightly damped room echo
    const reverbFilter = this.ctx.createBiquadFilter();
    reverbFilter.type = 'peaking';
    reverbFilter.frequency.setValueAtTime(1200, now);
    reverbFilter.Q.setValueAtTime(0.8, now);
    reverbFilter.gain.setValueAtTime(-3, now);

    carrier.connect(chimeGain);
    chimeGain.connect(reverbFilter);
    reverbFilter.connect(this.masterGain);

    // Subharmonic lower ring to give it metal body
    const lowRing = this.ctx.createOscillator();
    lowRing.type = 'sine';
    lowRing.frequency.setValueAtTime(460, now); // Octave below

    const lowGain = this.ctx.createGain();
    lowGain.gain.setValueAtTime(0, now);
    lowGain.gain.linearRampToValueAtTime(0.12, now + 0.01);
    lowGain.gain.exponentialRampToValueAtTime(0.0001, now + 2.0);

    lowRing.connect(lowGain);
    lowGain.connect(this.masterGain);

    // Start all
    carrier.start(now);
    modulator.start(now);
    lowRing.start(now);

    // Stop after echo fades
    carrier.stop(now + 3.2);
    modulator.stop(now + 3.2);
    lowRing.stop(now + 2.2);
  }

  /**
   * Adjusts the volume of the HVAC vents and fluorescent lights hum.
   * @param {number} percent - 0 to 100
   */
  setHumVolume(percent) {
    this.humMaxVolume = (percent / 100) * 0.45;
    if (this.initialized && this.humGain) {
      this.humGain.gain.linearRampToValueAtTime(this.humMaxVolume, this.ctx.currentTime + 0.1);
    }
  }

  /**
   * Adjusts the volume of footsteps.
   * @param {number} percent - 0 to 100
   */
  setFootstepVolume(percent) {
    this.footstepMaxVolume = (percent / 100) * 0.8;
  }

  /**
   * Initializes the HTML5 audio element for streaming the 8-minute ambient theme.
   * Connects it to the Web Audio Graph using a MediaElement source.
   */
  startAmbientMusicLoop() {
    if (this.musicAudio) return;

    try {
      this.musicAudio = new Audio('/Frontroom%20Drift.mp3');
      this.musicAudio.loop = true;
      this.musicAudio.crossOrigin = "anonymous";

      // Connect source node to context
      this.musicSource = this.ctx.createMediaElementSource(this.musicAudio);
      this.musicGain = this.ctx.createGain();
      this.musicGain.gain.setValueAtTime(0, this.ctx.currentTime);

      this.musicSource.connect(this.musicGain);
      this.musicGain.connect(this.masterGain);

      // Start the cyclic play scheduler loop
      this.runMusicCycle();
    } catch (e) {
      console.error("WebAudioSynth: Failed to setup ambient music.", e);
    }
  }

  /**
   * Looping cycle that seek to a random spot, fades music in (12s),
   * plays (40-60s), fades music out (12s), pauses, and waits (30-55s).
   */
  runMusicCycle() {
    if (!this.initialized || !this.musicAudio) return;

    // Reset timeouts just in case
    clearTimeout(this.musicTimeout);
    clearTimeout(this.musicSilenceTimeout);
    clearTimeout(this.musicNextCycleTimeout);

    try {
      // Seek to a random starting offset in the 8-minute song (480 seconds total)
      if (this.musicAudio.readyState >= 1) {
        const trackDuration = this.musicAudio.duration || 480;
        const maxStart = Math.max(0, trackDuration - 90);
        this.musicAudio.currentTime = Math.random() * maxStart;
      }
    } catch (e) {
      console.warn("WebAudioSynth: Could not seek music track:", e);
    }

    this.musicAudio.play().then(() => {
      this.musicPlayingState = 'playing';
      const now = this.ctx.currentTime;
      const fadeInTime = 12; // 12 seconds fade-in
      const playTime = 40 + Math.random() * 20; // 40 to 60 seconds play fragment

      // Smooth fade-in
      this.musicGain.gain.cancelScheduledValues(now);
      this.musicGain.gain.setValueAtTime(0, now);
      this.musicGain.gain.linearRampToValueAtTime(this.musicMaxVolume, now + fadeInTime);

      // Schedule transition to fade-out
      this.musicTimeout = setTimeout(() => {
        if (!this.initialized || !this.musicAudio) return;
        
        this.musicPlayingState = 'fading';
        const outNow = this.ctx.currentTime;
        const fadeOutTime = 12; // 12 seconds fade-out

        this.musicGain.gain.cancelScheduledValues(outNow);
        this.musicGain.gain.setValueAtTime(this.musicGain.gain.value, outNow);
        this.musicGain.gain.linearRampToValueAtTime(0, outNow + fadeOutTime);

        // Schedule pause & silence gap
        this.musicSilenceTimeout = setTimeout(() => {
          if (!this.initialized || !this.musicAudio) return;

          this.musicAudio.pause();
          this.musicPlayingState = 'silent';

          const silenceDuration = 30 + Math.random() * 25; // 30 to 55 seconds silence
          this.musicNextCycleTimeout = setTimeout(() => {
            this.runMusicCycle();
          }, silenceDuration * 1000);

        }, fadeOutTime * 1000);

      }, (fadeInTime + playTime) * 1000);

    }).catch(err => {
      console.warn("WebAudioSynth: Music playback failed or deferred:", err);
      // Retry in 8 seconds
      this.musicNextCycleTimeout = setTimeout(() => this.runMusicCycle(), 8000);
    });
  }

  /**
   * Adjusts the volume of the ambient theme music.
   * @param {number} percent - 0 to 100
   */
  setMusicVolume(percent) {
    this.musicMaxVolume = (percent / 100) * 0.3; // max out at 0.3 for perfect balance
    if (this.initialized && this.musicGain) {
      const now = this.ctx.currentTime;
      // If we are currently in 'playing' state, adjust active volume immediately
      if (this.musicPlayingState === 'playing') {
        this.musicGain.gain.linearRampToValueAtTime(this.musicMaxVolume, now + 0.2);
      }
    }
  }
}
